// WorkOS only accepts the exact registered callback URL, so the CAD launch
// query cannot ride along on the redirect URI. Instead it is held here for the
// duration of one SSO round trip, bound to the WorkOS state Scorpius issued.
//
// Lifecycle of one entry:
//   1. /irp-sso?query=... (CAD launch): after Scorpius returns the WorkOS
//      authorization URL and state, savePendingIRPLaunch(state, query).
//   2. /irp-sso?code=...&state=... (WorkOS callback): readPendingIRPLaunch
//      (state) recovers the query only if the returned state matches.
//   3. The attempt ends (iRP launched, or any terminal failure):
//      clearPendingIRPLaunch().
//
// Why sessionStorage: it survives the same-tab round trip through WorkOS and
// the IdP, is scoped to the tab that started the login (so two CAD launches in
// two tabs never read each other's query), and dies with the tab. Notifier's
// roster step-up SSO relies on the same property. The query is a caller phone
// number or alert ID, so it is also removed as soon as the attempt ends and
// pruned on read once expired.
//
// TODO: Scorpius keeps a single SSO state cookie per browser, so starting a
// second iRP SSO login in another tab before the first one returns makes the
// first callback fail Scorpius state validation. The user can retry from the
// restored launch URL; supporting concurrent logins would need per-attempt
// state cookies in Scorpius.
const PENDING_IRP_LAUNCH_KEY = 'irp-sso-pending-launch';

// Matches the Scorpius SSO state cookie max_age. Past this point the callback
// state can no longer be verified, so the launch context is useless anyway.
export const PENDING_IRP_LAUNCH_TTL_MS = 10 * 60 * 1000;

type PendingIRPLaunch = {
  state: string;
  query: string;
  expiresAt: number;
};

/**
 * Why a callback could not recover its launch. Reported to Sentry (never the
 * query or state themselves) so support can tell an expired login from a
 * different-tab or forged callback without needing a HAR from the user.
 */
export type PendingIRPLaunchMissReason =
  | 'missing_state'
  | 'not_found'
  | 'state_mismatch'
  | 'expired'
  | 'malformed'
  | 'storage_unavailable';

export type PendingIRPLaunchLookup =
  | { query: string; missReason: null }
  | { query: null; missReason: PendingIRPLaunchMissReason };

const miss = (
  missReason: PendingIRPLaunchMissReason,
): PendingIRPLaunchLookup => ({ query: null, missReason });

const isPendingIRPLaunch = (value: unknown): value is PendingIRPLaunch => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const { state, query, expiresAt } = value as Record<string, unknown>;
  // Empty strings or a non-finite expiry can only come from a corrupted or
  // hand-edited entry; savePendingIRPLaunch never writes them.
  return (
    typeof state === 'string' &&
    state.length > 0 &&
    typeof query === 'string' &&
    query.length > 0 &&
    typeof expiresAt === 'number' &&
    Number.isFinite(expiresAt)
  );
};

/**
 * Store the CAD launch query for the SSO attempt identified by `state`.
 * Only one attempt is kept per tab: a new attempt replaces the previous one,
 * mirroring Scorpius replacing its state cookie.
 * Throws if storage is unavailable (disabled, sandboxed, or over quota) so the
 * caller fails before leaving for WorkOS instead of returning to a callback
 * that cannot launch iRP.
 */
export const savePendingIRPLaunch = (
  state: string,
  query: string,
  storage: Storage = window.sessionStorage,
): void => {
  const pendingLaunch: PendingIRPLaunch = {
    state,
    query,
    expiresAt: Date.now() + PENDING_IRP_LAUNCH_TTL_MS,
  };
  storage.setItem(PENDING_IRP_LAUNCH_KEY, JSON.stringify(pendingLaunch));
};

/**
 * Remove this tab's launch context. Never throws: cleanup runs on failure
 * paths that must still reset the page even when storage is unavailable.
 */
export const clearPendingIRPLaunch = (
  storage: Storage = window.sessionStorage,
): void => {
  try {
    storage.removeItem(PENDING_IRP_LAUNCH_KEY);
  } catch {
    // Storage being unavailable means there is nothing to clear.
  }
};

/**
 * Look up the launch query for a WorkOS callback. It is returned only when it
 * belongs to this callback's state and has not expired; otherwise `query` is
 * null and `missReason` says why. Never throws. A miss must be treated as a
 * terminal error, never as a cue to fall back to another query source.
 *
 * A matching entry is not consumed here, so a remount while the code exchange
 * is in flight still sees the same launch context; callers clear it once the
 * attempt succeeds or fails. Expired or malformed entries can never be used,
 * so they are removed immediately. A live entry for a different state is left
 * alone: it may belong to the attempt this tab is still waiting on.
 *
 * Calling this repeatedly with the same input gives the same result (pruning
 * only removes entries that would miss anyway), so it is safe inside a React
 * state initializer that StrictMode invokes twice.
 *
 * The returned query is not validated as an iRP query; callers must still run
 * it through isValidIRPQuery before use.
 */
export const readPendingIRPLaunch = (
  state: string | null | undefined,
  storage: Storage = window.sessionStorage,
): PendingIRPLaunchLookup => {
  if (!state) {
    // Scorpius always sends state, so a stateless callback cannot be matched
    // to any attempt this tab started.
    return miss('missing_state');
  }

  let rawValue: string | null;
  try {
    rawValue = storage.getItem(PENDING_IRP_LAUNCH_KEY);
  } catch {
    return miss('storage_unavailable');
  }

  if (!rawValue) {
    return miss('not_found');
  }

  let pendingLaunch: unknown;
  try {
    pendingLaunch = JSON.parse(rawValue);
  } catch {
    pendingLaunch = null;
  }

  if (!isPendingIRPLaunch(pendingLaunch)) {
    clearPendingIRPLaunch(storage);
    return miss('malformed');
  }

  if (pendingLaunch.expiresAt <= Date.now()) {
    clearPendingIRPLaunch(storage);
    return miss('expired');
  }

  if (pendingLaunch.state !== state) {
    return miss('state_mismatch');
  }

  return { query: pendingLaunch.query, missReason: null };
};
